import { useState } from 'react';
import { useNotify } from 'react-admin';
import { API_BASE } from '../utils/apiClient';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { PastGames } from '../components/PastGames';
import { CompanionEditor } from '../components/CompanionEditor';
import type { CompanionData } from '@garuda/shared';
// ─── Helpers ─────────────────────────────────────────────

/** Inline slug util — avoids Vite CJS interop issue with @garuda/shared */
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// ─── Types ──────────────────────────────────────────────

interface TimelineMovie {
  id: string;
  title: string;
  slug: string;
  releaseDate: string;
}

// ─── Constants ──────────────────────────────────────────

const CATEGORIES = [
  'tamil', 'hindi', 'telugu', 'malayalam', 'kannada',
  'bengali', 'marathi', 'punjabi', 'gujarati', 'odia', 'assamese', 'english',
];

// ─── Component ──────────────────────────────────────────

export const TimelineCreate = () => {
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState('tamil');
  const [movieCount, setMovieCount] = useState(7);
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [curatedOnly, setCuratedOnly] = useState(true);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [movies, setMovies] = useState<TimelineMovie[]>([]);
  const [error, setError] = useState('');

  // SEO fields
  const [slug, setSlug] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  // Companion state
  const [companionData, setCompanionData] = useState<Partial<CompanionData> | null>(null);

  // Manual add state
  const [manualTitle, setManualTitle] = useState('');
  const [manualDate, setManualDate] = useState('');

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setMovies([]);

    try {
      const body: Record<string, unknown> = {
        entityType: 'movie',
        category,
        date,
        movieCount,
      };

      const options: Record<string, unknown> = {};
      if (yearFrom && yearTo && !isNaN(parseInt(yearFrom)) && !isNaN(parseInt(yearTo))) {
        options.yearRange = [parseInt(yearFrom), parseInt(yearTo)];
      }
      if (curatedOnly) options.curatedOnly = true;
      if (Object.keys(options).length > 0) body.options = options;

      const res = await fetch(`${API_BASE}/admin/api/games/timeline/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let message = 'Generation failed';
        try {
          const data = await res.json();
          message = data.message || message;
        } catch { /* non-JSON */ }
        setError(message);
        return;
      }

      const data = await res.json();
      setMovies(data.movies || []);
      notify(`Generated ${data.movies?.length || 0} movies!`, { type: 'success' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (movies.length < 3) {
      notify('Need at least 3 movies', { type: 'warning' });
      return;
    }

    setSaving(true);

    try {
      const body: Record<string, unknown> = {
        entityType: 'movie',
        category,
        date,
        movies,
      };
      if (slug.trim()) body.slug = slug.trim();
      if (seoDescription.trim()) body.seo_description = seoDescription.trim();
      if (companionData?.headline) body.companion = companionData;

      const res = await fetch(`${API_BASE}/admin/api/games/timeline`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let message = 'Failed to save';
        try {
          const data = await res.json();
          message = data.message || message;
        } catch { /* non-JSON */ }
        throw new Error(message);
      }

      notify('Timeline game saved!', { type: 'success' });
      window.location.href = '#/game-configs';
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to save', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMovie = (index: number) => {
    setMovies((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveMovie = (index: number, direction: 'up' | 'down') => {
    setMovies((prev) => {
      const updated = [...prev];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= updated.length) return prev;
      [updated[index], updated[target]] = [updated[target], updated[index]];
      return updated;
    });
  };

  const handleEditMovie = (index: number, field: keyof TimelineMovie, value: string) => {
    setMovies((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Re-derive slug when title changes
      if (field === 'title') {
        updated[index] = { ...updated[index], slug: toSlug(value) };
      }
      // Re-sort by release date if date was changed
      if (field === 'releaseDate') {
        updated.sort((a, b) => a.releaseDate.localeCompare(b.releaseDate));
      }
      return updated;
    });
  };

  const handleAddMovie = () => {
    if (!manualTitle || !manualDate) {
      notify('Title and release date are required', { type: 'warning' });
      return;
    }
    const newMovie: TimelineMovie = {
      id: crypto.randomUUID(),
      title: manualTitle,
      slug: toSlug(manualTitle),
      releaseDate: manualDate,
    };
    setMovies((prev) => [...prev, newMovie].sort((a, b) => a.releaseDate.localeCompare(b.releaseDate)));
    setManualTitle('');
    setManualDate('');
  };

  const canSave = movies.length >= 3 && !saving && !generating;

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => (window.location.href = '#/game-configs')}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Create Timeline Game
        </Typography>
        {activeTab === 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? 'Saving...' : 'Save Game'}
          </Button>
        )}
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Create" />
        <Tab label="Past Games" />
      </Tabs>

      {activeTab === 1 && <PastGames gameType="timeline" category={category} />}

      {activeTab === 0 && (<>

      {/* Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Game Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  label="Category"
                >
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="Movie Count"
                type="number"
                value={movieCount}
                onChange={(e) => setMovieCount(parseInt(e.target.value) || 7)}
                fullWidth
                size="small"
                inputProps={{ min: 3, max: 15 }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="Year From"
                type="number"
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText="e.g. 2000"
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="Year To"
                type="number"
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText="e.g. 2010"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={generating ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                onClick={handleGenerate}
                disabled={generating}
                fullWidth
                sx={{ height: 40 }}
              >
                {generating ? 'Generating...' : 'Generate'}
              </Button>
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Checkbox
                checked={curatedOnly}
                onChange={(e) => setCuratedOnly(e.target.checked)}
                size="small"
              />
            }
            label="Curated movies only"
            sx={{ mt: 1 }}
          />
        </CardContent>
      </Card>

      {/* SEO Fields */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SEO (Optional — auto-generated if empty)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                fullWidth
                size="small"
                helperText="URL-friendly identifier (e.g., timeline-tamil-7-movies-may-10-2026)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SEO Description"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Search engine description (100-300 chars)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Companion Article */}
      <Box sx={{ mb: 3 }}>
        <CompanionEditor
          value={companionData}
          onChange={setCompanionData}
          gameType="timeline"
          items={movies.map((m) => ({ title: m.title }))}
        />
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Movies Table */}
      {movies.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Movies ({movies.length}) — Chronological Order
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>#</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell width={130}>Release Date</TableCell>
                    <TableCell width={120} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movies.map((movie, index) => (
                    <TableRow key={movie.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <TextField
                          value={movie.title}
                          onChange={(e) => handleEditMovie(index, 'title', e.target.value)}
                          variant="standard"
                          fullWidth
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={movie.releaseDate}
                          onChange={(e) => handleEditMovie(index, 'releaseDate', e.target.value)}
                          variant="standard"
                          fullWidth
                          size="small"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleMoveMovie(index, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveMovie(index, 'down')}
                          disabled={index === movies.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMovie(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Manual Add */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Movie Manually
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                label="Movie Title"
                value={manualTitle}
                onChange={(e) => setManualTitle(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Release Date"
                type="date"
                value={manualDate}
                onChange={(e) => setManualDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddMovie}
                fullWidth
                sx={{ height: 40 }}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      </>)}
    </Box>
  );
};
